/**
 * React Query Key Factory
 *
 * Provides consistent, type-safe query keys for React Query cache management.
 * Follows the hierarchical key structure pattern for easy invalidation.
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys
 */

export const queryKeys = {
  /**
   * Moves query keys
   */
  moves: {
    all: ['moves'] as const,
    lists: () => [...queryKeys.moves.all, 'list'] as const,
    list: (filters?: {
      searchText?: string
      limit?: number
      offset?: number
      includeRelations?: boolean
    }) => [...queryKeys.moves.lists(), filters] as const,
    details: () => [...queryKeys.moves.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.moves.details(), id] as const,
  },

  /**
   * Series query keys
   */
  series: {
    all: ['series'] as const,
    lists: () => [...queryKeys.series.all, 'list'] as const,
    list: (filters?: {
      searchText?: string
      limit?: number
      offset?: number
      includeRelations?: boolean
    }) => [...queryKeys.series.lists(), filters] as const,
    details: () => [...queryKeys.series.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.series.details(), id] as const,
  },

  /**
   * Tags query keys
   */
  tags: {
    all: ['tags'] as const,
    lists: () => [...queryKeys.tags.all, 'list'] as const,
    list: (filters?: {
      searchText?: string
      limit?: number
      offset?: number
    }) => [...queryKeys.tags.lists(), filters] as const,
    details: () => [...queryKeys.tags.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tags.details(), id] as const,
    statistics: (id: string) =>
      [...queryKeys.tags.detail(id), 'statistics'] as const,
  },

  /**
   * Move Connections query keys
   */
  moveConnections: {
    all: ['moveConnections'] as const,
    bySeries: (seriesId: string) =>
      [...queryKeys.moveConnections.all, 'series', seriesId] as const,
  },

  /**
   * Taggings query keys (polymorphic tagging system)
   */
  taggings: {
    all: ['taggings'] as const,
    lists: () => [...queryKeys.taggings.all, 'list'] as const,
    list: (filters?: {
      tagId?: string
      taggableType?: 'MOVE' | 'SERIES' | 'move' | 'series'
      taggableId?: string
      limit?: number
      offset?: number
    }) => [...queryKeys.taggings.lists(), filters] as const,
    details: () => [...queryKeys.taggings.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.taggings.details(), id] as const,
    byEntity: (
      taggableType: 'MOVE' | 'SERIES' | 'move' | 'series',
      taggableId: string
    ) =>
      [
        ...queryKeys.taggings.all,
        'entity',
        taggableType.toUpperCase(),
        taggableId,
      ] as const,
  },

  /**
   * Dashboard statistics query keys
   */
  dashboard: {
    all: ['dashboard'] as const,
    statistics: () => [...queryKeys.dashboard.all, 'statistics'] as const,
    recentMoves: (limit?: number) =>
      [...queryKeys.dashboard.all, 'recentMoves', limit] as const,
    recentSeries: (limit?: number) =>
      [...queryKeys.dashboard.all, 'recentSeries', limit] as const,
  },
} as const

/**
 * Type-safe query key accessors
 * Export individual key factories for convenience
 */
export const movesKeys = queryKeys.moves
export const seriesKeys = queryKeys.series
export const tagsKeys = queryKeys.tags
export const moveConnectionsKeys = queryKeys.moveConnections
export const taggingsKeys = queryKeys.taggings
export const dashboardKeys = queryKeys.dashboard
